/**
 * Calculates the actual height of the header dynamically
 * This accounts for the top bar and main navigation bar
 * @returns {number} The total height of the header in pixels
 */
export const getHeaderHeight = () => {
  // Try to get the actual header element
  const header = document.querySelector('header');
  if (header) {
    // Use getBoundingClientRect for more accurate height measurement
    // This accounts for borders and any box-sizing differences
    const headerRect = header.getBoundingClientRect();
    if (headerRect.height > 0) {
      return headerRect.height;
    }
    // Fallback to offsetHeight if getBoundingClientRect doesn't work
    if (header.offsetHeight > 0) {
      return header.offsetHeight;
    }
  }
  
  // Fallback calculation based on known structure
  // Top bar: py-2.5 sm:py-3 (responsive padding ~10-12px top/bottom) + content (~20-24px)
  // Main nav: h-16 lg:h-20 (64px mobile, 80px desktop)
  // Total approximate: ~40-48px (top bar) + 64-80px (main nav) = ~104-128px
  const isLargeScreen = window.innerWidth >= 1024;
  // Use slightly higher values to ensure proper spacing
  return isLargeScreen ? 128 : 112;
};

/**
 * Waits for an element to be ready and stable in the DOM
 * @param {HTMLElement} element - The element to wait for
 * @param {number} maxWaitTime - Maximum time to wait in ms (default: 3000)
 * @returns {Promise<boolean>} - True if element is ready, false if timeout
 */
const waitForElementReady = (element, maxWaitTime = 3000) => {
  return new Promise((resolve) => {
    if (!element) {
      resolve(false);
      return;
    }

    const startTime = Date.now();
    let lastHeight = 0;
    let lastTop = 0;
    let stableCount = 0;
    const requiredStableChecks = 3; // Element must be stable for 3 consecutive checks

    const checkElement = () => {
      if (Date.now() - startTime > maxWaitTime) {
        resolve(true); // Timeout but element exists, proceed anyway
        return;
      }

      const rect = element.getBoundingClientRect();
      const currentHeight = rect.height;
      const currentTop = rect.top;

      // Check if element position and size are stable
      if (Math.abs(currentHeight - lastHeight) < 1 && Math.abs(currentTop - lastTop) < 1) {
        stableCount++;
        if (stableCount >= requiredStableChecks) {
          resolve(true);
          return;
        }
      } else {
        stableCount = 0;
      }

      lastHeight = currentHeight;
      lastTop = currentTop;

      // Check again after a short delay
      requestAnimationFrame(() => {
        setTimeout(checkElement, 50);
      });
    };

    // Start checking
    requestAnimationFrame(checkElement);
  });
};

/**
 * Scrolls to an element, positioning it at the top of the viewport
 * Handles delayed content loading with retry logic
 * @param {string|HTMLElement} elementOrSelector - Element ID, selector, or HTMLElement
 * @param {Object} options - Scroll options
 * @param {number} options.offset - Additional offset to subtract from scroll position (default: 0)
 * @param {boolean} options.smooth - Use smooth scrolling (default: true)
 * @param {number} options.maxRetries - Maximum number of retry attempts (default: 10)
 * @param {number} options.retryDelay - Delay between retries in ms (default: 200)
 * @returns {Promise<boolean>} - True if scroll was successful, false otherwise
 */
export const scrollToElement = async (elementOrSelector, options = {}) => {
  const { 
    offset = 0, 
    smooth = true, 
    maxRetries = 10, 
    retryDelay = 200 
  } = options;
  
  let element;
  let selector;
  
  // Find element with retry logic
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (typeof elementOrSelector === 'string') {
      // Remove leading # if present (handles both '#contact' and 'contact')
      selector = elementOrSelector.startsWith('#') 
        ? elementOrSelector.substring(1) 
        : elementOrSelector;
      
      // Try as ID first, then as selector
      element = document.getElementById(selector) || 
                document.querySelector(`#${selector}`) || 
                document.querySelector(elementOrSelector);
    } else if (elementOrSelector instanceof HTMLElement) {
      element = elementOrSelector;
    }
    
    if (element) {
      // Element found, wait for it to be stable
      const isReady = await waitForElementReady(element, 2000);
      if (isReady) {
        break;
      }
    }
    
    // If element not found or not ready, wait and retry
    if (attempt < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
    }
  }
  
  if (!element) {
    return false;
  }
  
  // Wait for images and other resources to potentially load
  await new Promise(resolve => {
    if (document.readyState === 'complete') {
      resolve();
    } else {
      window.addEventListener('load', resolve, { once: true });
      // Fallback timeout
      setTimeout(resolve, 500);
    }
  });
  
  // Final check - ensure element is still in DOM and has valid position
  const elementRect = element.getBoundingClientRect();
  if (elementRect.height === 0 && elementRect.width === 0) {
    // Element might be hidden or not rendered yet
    await new Promise(resolve => setTimeout(resolve, 300));
  }
  
  // Calculate the exact scroll position needed
  const finalRect = element.getBoundingClientRect();
  const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const elementTop = finalRect.top + currentScrollTop;
  
  // Scroll so the section starts at the top of the page
  // The fixed header will overlay the content, but the section itself starts from page top
  const scrollPosition = elementTop - offset;
  
  window.scrollTo({
    top: Math.max(0, scrollPosition),
    behavior: smooth ? 'smooth' : 'auto'
  });
  
  return true;
};

